import { getTenant } from '../../../theme/tokens'
// Transaction scenario fixtures for testing and development
// Each scenario is self-contained with transaction metadata and ledger rows

import type { Transaction, LedgerRow } from './transactionViewModel'
import { getRowNetImpact } from './transactionViewModel'
export interface TransactionScenario {
  scenarioId: string
  description: string
  transaction: Transaction
  ledgerRows: LedgerRow[]
}

// Normalize ledger rows to ensure settlement.amountInBucket matches computed net impact
// This prevents fixtures from having inconsistent amountInBucket values
function normalizeLedgerRows(rows: LedgerRow[]): LedgerRow[] {
  return rows.map((row) => {
    if (!row.settlement) return row

    // Override amountInBucket with computed net impact for consistency
    return {
      ...row,
      settlement: {
        ...row.settlement,
        amountInBucket: getRowNetImpact(row),
      },
    }
  })
}

// Scenario 1: Payment only + reserve hold
const SCENARIO_PAYMENT_WITH_RESERVE: TransactionScenario = {
  scenarioId: 'payment-with-reserve',
  description: 'Simple payment with 20% reserve hold',
  transaction: {
    id: '1',
    transactionId: 'TXN-2026-001',
    amount: 500,
    currency: getTenant().currency.code,
    date: '2026-05-01T10:00:00',
    methodType: 'card',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 500,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/05/2026 10:00',
      settlement: {
        kind: 'payout',
        id: 'po_10001',
        date: '03/05/2026',
        status: 'Pending',
        amountInBucket: 500,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'GATEWAY_FEE',
      calcStatus: 'Actual',
      gross: null,
      feeExTax: -5,
      taxAmount: -0.4,
      status: 'Posted',
      effectiveAt: '01/05/2026 10:01',
      settlement: {
        kind: 'payout',
        id: 'po_10001',
        date: '03/05/2026',
        status: 'Pending',
        amountInBucket: -5.4,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -5,
            taxAmount: -0.4,
            feeCode: 'GATEWAY_FEE',
            calcStatus: 'Actual',
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/05/2026 10:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/05/2026',
        status: 'Active',
        amountInBucket: -100,
      },
      feeBreakdown: null,
    },
    {
      id: 'L4',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Pending',
      effectiveAt: '31/05/2026 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/05/2026',
        status: 'Pending',
        amountInBucket: 100,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 2: Payment + reserve hold + partial refund
const SCENARIO_PARTIAL_REFUND: TransactionScenario = {
  scenarioId: 'partial-refund',
  description: 'Payment with reserve, then partial refund',
  transaction: {
    id: '2',
    transactionId: 'TXN-2026-002',
    amount: 800,
    currency: getTenant().currency.code,
    date: '2026-05-01T14:00:00',
    methodType: 'card',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 800,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/05/2026 14:00',
      settlement: {
        kind: 'payout',
        id: 'po_10002',
        date: '03/05/2026',
        status: 'Pending',
        amountInBucket: 800,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'GATEWAY_FEE',
      calcStatus: 'Actual',
      gross: null,
      feeExTax: -8,
      taxAmount: -0.64,
      status: 'Posted',
      effectiveAt: '01/05/2026 14:01',
      settlement: {
        kind: 'payout',
        id: 'po_10002',
        date: '03/05/2026',
        status: 'Pending',
        amountInBucket: -8.64,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -8,
            taxAmount: -0.64,
            feeCode: 'GATEWAY_FEE',
            calcStatus: 'Actual',
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/05/2026 14:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_002',
        releaseDate: '31/05/2026',
        status: 'Active',
        amountInBucket: -160,
      },
      feeBreakdown: null,
    },
    {
      id: 'L4',
      posting: 'Partial refund',
      attemptId: null,
      type: 'refund',
      gross: -300,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '02/05/2026 10:30',
      settlement: {
        kind: 'payout',
        id: 'po_10003',
        date: '04/05/2026',
        status: 'Pending',
        amountInBucket: -300,
      },
      feeBreakdown: null,
    },
    {
      id: 'L5',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Pending',
      effectiveAt: '31/05/2026 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_002',
        releaseDate: '31/05/2026',
        status: 'Pending',
        amountInBucket: 160,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 3: Payment + full refund
const SCENARIO_FULL_REFUND: TransactionScenario = {
  scenarioId: 'full-refund',
  description: 'Payment fully refunded',
  transaction: {
    id: '3',
    transactionId: 'TXN-2026-003',
    amount: 1200,
    currency: getTenant().currency.code,
    date: '2026-05-05T09:00:00',
    methodType: 'card',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 1200,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '05/05/2026 09:00',
      settlement: {
        kind: 'payout',
        id: 'po_10004',
        date: '07/05/2026',
        status: 'Pending',
        amountInBucket: 1200,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'GATEWAY_FEE',
      calcStatus: 'Actual',
      gross: null,
      feeExTax: -12,
      taxAmount: -0.96,
      status: 'Posted',
      effectiveAt: '05/05/2026 09:01',
      settlement: {
        kind: 'payout',
        id: 'po_10004',
        date: '07/05/2026',
        status: 'Pending',
        amountInBucket: -12.96,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -12,
            taxAmount: -0.96,
            feeCode: 'GATEWAY_FEE',
            calcStatus: 'Actual',
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Interchange fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'IC_INTERCHANGE',
      calcStatus: 'Estimated',
      gross: null,
      feeExTax: -18,
      taxAmount: -1.44,
      status: 'Posted',
      effectiveAt: '06/05/2026 08:00',
      settlement: {
        kind: 'payout',
        id: 'po_10005',
        date: '08/05/2026',
        status: 'Pending',
        amountInBucket: -19.44,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Interchange',
            amountExTax: -18,
            taxAmount: -1.44,
            feeCode: 'IC_INTERCHANGE',
            calcStatus: 'Estimated',
            rateDeterminants: {
              scheme: 'Mastercard',
              cardProduct: 'World',
              domesticOrCrossBorder: 'Domestic',
              mccOrProgram: 'MCC 5732 — Electronics',
              ruleId: 'IC-MY-WORLD-DOM',
            },
          },
        ],
      },
    },
    {
      id: 'L4',
      posting: 'Full refund',
      attemptId: null,
      type: 'refund',
      gross: -1200,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '06/05/2026 15:00',
      settlement: {
        kind: 'payout',
        id: 'po_10006',
        date: '08/05/2026',
        status: 'Pending',
        amountInBucket: -1200,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 4: Payment + partial dispute
const SCENARIO_PARTIAL_DISPUTE: TransactionScenario = {
  scenarioId: 'partial-dispute',
  description: 'Payment with partial dispute (not yet resolved)',
  transaction: {
    id: '4',
    transactionId: 'TXN-2026-004',
    amount: 2000,
    currency: getTenant().currency.code,
    date: '2026-04-28T11:00:00',
    methodType: 'card',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 2000,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '28/04/2026 11:00',
      settlement: {
        kind: 'payout',
        id: 'po_10007',
        date: '30/04/2026',
        status: 'Pending',
        amountInBucket: 2000,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'GATEWAY_FEE',
      calcStatus: 'Actual',
      gross: null,
      feeExTax: -20,
      taxAmount: -1.6,
      status: 'Posted',
      effectiveAt: '28/04/2026 11:01',
      settlement: {
        kind: 'payout',
        id: 'po_10007',
        date: '30/04/2026',
        status: 'Pending',
        amountInBucket: -21.6,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -20,
            taxAmount: -1.6,
            feeCode: 'GATEWAY_FEE',
            calcStatus: 'Actual',
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Interchange fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'IC_INTERCHANGE',
      calcStatus: 'Estimated',
      gross: null,
      feeExTax: -30,
      taxAmount: -2.4,
      status: 'Posted',
      effectiveAt: '29/04/2026 09:00',
      settlement: {
        kind: 'payout',
        id: 'po_10008',
        date: '01/05/2026',
        status: 'Pending',
        amountInBucket: -32.4,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Interchange',
            amountExTax: -30,
            taxAmount: -2.4,
            feeCode: 'IC_INTERCHANGE',
            calcStatus: 'Estimated',
            rateDeterminants: {
              scheme: 'Visa',
              cardProduct: 'Signature',
              domesticOrCrossBorder: 'Domestic',
              mccOrProgram: 'MCC 5814 — Fast Food',
              ruleId: 'IC-MY-SIG-DOM',
            },
          },
        ],
      },
    },
    {
      id: 'L4',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '28/04/2026 11:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_004',
        releaseDate: '28/05/2026',
        status: 'Active',
        amountInBucket: -400,
      },
      feeBreakdown: null,
    },
    {
      id: 'L5',
      posting: 'Dispute received',
      attemptId: null,
      type: 'dispute',
      gross: -600,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '03/05/2026 14:00',
      settlement: {
        kind: 'payout',
        id: 'po_10009',
        date: '05/05/2026',
        status: 'Pending',
        amountInBucket: -600,
      },
      feeBreakdown: null,
    },
    {
      id: 'L6',
      posting: 'Dispute fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'DISPUTE_COST',
      calcStatus: 'Actual',
      gross: null,
      feeExTax: -50,
      taxAmount: -4,
      status: 'Posted',
      effectiveAt: '03/05/2026 14:01',
      settlement: {
        kind: 'payout',
        id: 'po_10009',
        date: '05/05/2026',
        status: 'Pending',
        amountInBucket: -54,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Dispute handling fee',
            amountExTax: -50,
            taxAmount: -4,
            feeCode: 'DISPUTE_COST',
            calcStatus: 'Actual',
          },
        ],
      },
    },
    {
      id: 'L7',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Pending',
      effectiveAt: '28/05/2026 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_004',
        releaseDate: '28/05/2026',
        status: 'Pending',
        amountInBucket: 400,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 5: Complex scenario
const SCENARIO_COMPLEX: TransactionScenario = {
  scenarioId: 'complex-dispute-won',
  description:
    'Complex: payment, reserve, partial refund, dispute received and won',
  transaction: {
    id: '5',
    transactionId: 'TXN-2026-005',
    amount: 1000,
    currency: getTenant().currency.code,
    date: '2026-05-01T10:00:00',
    methodType: 'card',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 1000,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/05/2026 10:00',
      settlement: {
        kind: 'payout',
        id: 'po_12345',
        date: '03/05/2026',
        status: 'Pending',
        amountInBucket: 1000,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'GATEWAY_FEE',
      calcStatus: 'Actual',
      gross: null,
      feeExTax: -1,
      taxAmount: -0.08,
      status: 'Posted',
      effectiveAt: '01/05/2026 10:01',
      settlement: {
        kind: 'payout',
        id: 'po_12345',
        date: '03/05/2026',
        status: 'Pending',
        amountInBucket: -1.08,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -1,
            taxAmount: -0.08,
            feeCode: 'GATEWAY_FEE',
            calcStatus: 'Actual',
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/05/2026 10:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/05/2026',
        status: 'Active',
        amountInBucket: -200,
      },
      feeBreakdown: null,
    },
    {
      id: 'L4',
      posting: 'Partial refund',
      attemptId: null,
      type: 'refund',
      gross: -200,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '02/05/2026 14:30',
      settlement: {
        kind: 'payout',
        id: 'po_12346',
        date: '04/05/2026',
        status: 'Pending',
        amountInBucket: -200,
      },
      feeBreakdown: null,
    },
    {
      id: 'L5',
      posting: 'Interchange fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'IC_INTERCHANGE',
      calcStatus: 'Estimated',
      gross: null,
      feeExTax: -15,
      taxAmount: -1.2,
      status: 'Posted',
      effectiveAt: '03/05/2026 09:00',
      settlement: {
        kind: 'payout',
        id: 'po_12347',
        date: '05/05/2026',
        status: 'Pending',
        amountInBucket: -16.2,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Interchange',
            amountExTax: -15,
            taxAmount: -1.2,
            feeCode: 'IC_INTERCHANGE',
            calcStatus: 'Estimated',
            rateDeterminants: {
              scheme: 'Mastercard',
              cardProduct: 'Standard',
              domesticOrCrossBorder: 'Domestic',
              mccOrProgram: 'MCC 5732 — Electronics',
              ruleId: 'IC-MY-STD-DOM',
            },
          },
        ],
      },
    },
    {
      id: 'L6',
      posting: 'Scheme fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'SCHEME_FEE_TXN',
      calcStatus: 'Estimated',
      gross: null,
      feeExTax: -10,
      taxAmount: -0.8,
      status: 'Posted',
      effectiveAt: '03/05/2026 09:01',
      settlement: {
        kind: 'payout',
        id: 'po_12347',
        date: '05/05/2026',
        status: 'Pending',
        amountInBucket: -10.8,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Card scheme fee',
            amountExTax: -10,
            taxAmount: -0.8,
            feeCode: 'SCHEME_FEE_TXN',
            calcStatus: 'Estimated',
            rateDeterminants: {
              scheme: 'Mastercard',
              cardProduct: 'Standard',
              domesticOrCrossBorder: 'Domestic',
              mccOrProgram: 'MCC 5732 — Electronics',
              ruleId: 'SCH-MY-MC-DOM',
            },
          },
        ],
      },
    },
    {
      id: 'L7',
      posting: 'Dispute received',
      attemptId: null,
      type: 'dispute',
      gross: -400,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '05/05/2026 11:00',
      settlement: {
        kind: 'payout',
        id: 'po_12348',
        date: '07/05/2026',
        status: 'Pending',
        amountInBucket: -400,
      },
      feeBreakdown: null,
    },
    {
      id: 'L8',
      posting: 'Dispute fee',
      attemptId: null,
      type: 'fee',
      feeCode: 'DISPUTE_COST',
      calcStatus: 'Actual',
      gross: null,
      feeExTax: -45,
      taxAmount: -3.6,
      status: 'Posted',
      effectiveAt: '05/05/2026 11:01',
      settlement: {
        kind: 'payout',
        id: 'po_12348',
        date: '07/05/2026',
        status: 'Pending',
        amountInBucket: -48.6,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Dispute handling fee',
            amountExTax: -45,
            taxAmount: -3.6,
            feeCode: 'DISPUTE_COST',
            calcStatus: 'Actual',
          },
        ],
      },
    },
    {
      id: 'L9',
      posting: 'Dispute won - funds returned',
      attemptId: null,
      type: 'dispute_reversal',
      gross: 400,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '13/05/2026 16:00',
      settlement: {
        kind: 'payout',
        id: 'po_12349',
        date: '15/05/2026',
        status: 'Pending',
        amountInBucket: 400,
      },
      feeBreakdown: null,
    },
    {
      id: 'L10',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Pending',
      effectiveAt: '31/05/2026 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/05/2026',
        status: 'Pending',
        amountInBucket: 200,
      },
      feeBreakdown: null,
    },
  ],
}

// All scenarios
export const SCENARIOS: TransactionScenario[] = [
  SCENARIO_PAYMENT_WITH_RESERVE,
  SCENARIO_PARTIAL_REFUND,
  SCENARIO_FULL_REFUND,
  SCENARIO_PARTIAL_DISPUTE,
  SCENARIO_COMPLEX,
]

// Helper to get a scenario by ID
export function buildScenario(scenarioId: string): TransactionScenario {
  const scenario = SCENARIOS.find((s) => s.scenarioId === scenarioId)
  if (!scenario) {
    throw new Error(`Scenario not found: ${scenarioId}`)
  }
  // Normalize ledger rows to ensure consistency
  return {
    ...scenario,
    ledgerRows: normalizeLedgerRows(scenario.ledgerRows),
  }
}

// Helper to get scenario by index (for easy switching)
export function getScenarioByIndex(index: number): TransactionScenario {
  if (index < 0 || index >= SCENARIOS.length) {
    throw new Error(`Invalid scenario index: ${index}`)
  }
  return SCENARIOS[index]
}

// Default scenario (complex one for demo)
export const DEFAULT_SCENARIO_ID = 'complex-dispute-won'
